import { Vector2 } from './Vector2';
import { TILE_SIZE } from '@hamsters/game-config';

export interface AABB {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Circle {
  x: number;
  y: number;
  radius: number;
}

export interface Ray {
  origin: Vector2;
  direction: Vector2;
  maxDistance: number;
}

export function circleVsAABB(circle: Circle, aabb: AABB): boolean {
  const closestX = Math.max(aabb.x, Math.min(circle.x, aabb.x + aabb.width));
  const closestY = Math.max(aabb.y, Math.min(circle.y, aabb.y + aabb.height));

  const dx = circle.x - closestX;
  const dy = circle.y - closestY;

  return dx * dx + dy * dy <= circle.radius * circle.radius;
}

export function circleVsCircle(a: Circle, b: Circle): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const distSq = dx * dx + dy * dy;
  const radiusSum = a.radius + b.radius;
  return distSq <= radiusSum * radiusSum;
}

export function rayVsAABB(ray: Ray, aabb: AABB): number | null {
  let tmin = -Infinity;
  let tmax = Infinity;

  if (ray.direction.x !== 0) {
    const t1 = (aabb.x - ray.origin.x) / ray.direction.x;
    const t2 = (aabb.x + aabb.width - ray.origin.x) / ray.direction.x;
    tmin = Math.max(tmin, Math.min(t1, t2));
    tmax = Math.min(tmax, Math.max(t1, t2));
  } else if (ray.origin.x < aabb.x || ray.origin.x > aabb.x + aabb.width) {
    return null;
  }

  if (ray.direction.y !== 0) {
    const t1 = (aabb.y - ray.origin.y) / ray.direction.y;
    const t2 = (aabb.y + aabb.height - ray.origin.y) / ray.direction.y;
    tmin = Math.max(tmin, Math.min(t1, t2));
    tmax = Math.min(tmax, Math.max(t1, t2));
  } else if (ray.origin.y < aabb.y || ray.origin.y > aabb.y + aabb.height) {
    return null;
  }

  if (tmax < 0 || tmin > tmax || tmin > ray.maxDistance) {
    return null;
  }

  return tmin;
}

export function raycast(
  origin: Vector2,
  direction: Vector2,
  walls: AABB[],
  maxDistance: number,
): { hit: boolean; distance: number; point: Vector2 } {
  const normDir = direction.clone().normalize();
  let closestT = maxDistance;
  let hit = false;

  for (const wall of walls) {
    const t = rayVsAABB(
      { origin, direction: normDir, maxDistance },
      wall,
    );
    if (t !== null && t < closestT) {
      closestT = t;
      hit = true;
    }
  }

  return {
    hit,
    distance: closestT,
    point: new Vector2(
      origin.x + normDir.x * closestT,
      origin.y + normDir.y * closestT,
    ),
  };
}

// Spatial hash for broad phase collision
export class SpatialHash {
  private cellSize: number;
  private cells = new Map<string, number[]>();

  constructor(cellSize = 128) {
    this.cellSize = cellSize;
  }

  clear(): void {
    this.cells.clear();
  }

  insert(entityId: number, aabb: AABB): void {
    const minX = Math.floor(aabb.x / this.cellSize);
    const maxX = Math.floor((aabb.x + aabb.width) / this.cellSize);
    const minY = Math.floor(aabb.y / this.cellSize);
    const maxY = Math.floor((aabb.y + aabb.height) / this.cellSize);

    for (let cx = minX; cx <= maxX; cx++) {
      for (let cy = minY; cy <= maxY; cy++) {
        const key = `${cx},${cy}`;
        const cell = this.cells.get(key);
        if (cell) {
          cell.push(entityId);
        } else {
          this.cells.set(key, [entityId]);
        }
      }
    }
  }

  query(aabb: AABB): number[] {
    const result = new Set<number>();
    const minX = Math.floor(aabb.x / this.cellSize);
    const maxX = Math.floor((aabb.x + aabb.width) / this.cellSize);
    const minY = Math.floor(aabb.y / this.cellSize);
    const maxY = Math.floor((aabb.y + aabb.height) / this.cellSize);

    for (let cx = minX; cx <= maxX; cx++) {
      for (let cy = minY; cy <= maxY; cy++) {
        const key = `${cx},${cy}`;
        const cell = this.cells.get(key);
        if (cell) {
          for (const id of cell) {
            result.add(id);
          }
        }
      }
    }

    return Array.from(result);
  }

  queryRadius(x: number, y: number, radius: number): number[] {
    return this.query({
      x: x - radius,
      y: y - radius,
      width: radius * 2,
      height: radius * 2,
    });
  }
}

// Build collision geometry from tilemap
export function buildCollisionWalls(
  collisionLayer: number[][],
  tileSize: number = TILE_SIZE,
): AABB[] {
  const walls: AABB[] = [];

  for (let y = 0; y < collisionLayer.length; y++) {
    const row = collisionLayer[y];
    if (!row) continue;
    for (let x = 0; x < row.length; x++) {
      if (row[x] === 1) {
        walls.push({
          x: x * tileSize,
          y: y * tileSize,
          width: tileSize,
          height: tileSize,
        });
      }
    }
  }

  return walls;
}
